import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc, Chart, Product } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import useProducts from '../../../../hooks/useProducts';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { ProductCard } from '../../../../components/products/ProductCard';
import { Button } from '../../../../components';

import { Unauthorized } from '../../../../components/Unauthorized';
import { Heading } from '../../../../components/Heading';
import { classNames } from '../../../../style/classNames';

export const Route = createFileRoute('/_authenticated/$patientId/onboarding/select')({
  component: OnboardingSelect,
});

function OnboardingSelect() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const { mutate: mutateProductInfo } = useUpdateChartSection();
  const [selected, setSelected] = React.useState<string>('');
  const [productConfirmed, setProductConfirmed] = React.useState<boolean>(false);
  const { isFetched: chartIsFetched } = chartQuery;
  const productsDocQuery = useProducts();
  const products = productsDocQuery.data as Product[];
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;

  // Access properties of the current location
  const pathname = currentLocation.pathname;
  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  const updateNavType = () => {
    mutatePatientDoc({
      patientId: patientId,
      navType: {
        ...patientInfo.navType,
        base: selected.toLowerCase().includes('self') ? 'self' : 'concierge',
      },
    });
  };

  const handleProductUpdate = () => {
    mutateProductInfo(
      {
        patientId: patientId,
        docData: {
          PRODUCT_INFO: {
            returning: true,
            selectedProduct: selected,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          updateNavType();
        },
        onError: () => {
          notifyChartSaveError();
          setProductConfirmed(false);
        },
      },
    );
  };

  const handleSelection = (name: string) => {
    switch (name) {
      case 'Self Guided Package':
        setSelected('Self Guided Package');
        break;
      case 'Vital Package':
        setSelected('Vital Package');
        break;
      case 'Physician Guided Package':
        setSelected('Physician Guided Package');
        break;
      default:
        break;
    }
  };

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (products && chart) {
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          setSelected('Self Guided Package');
          break;
        case 'vital':
          setSelected('Vital Package');
          break;
        case 'vip':
          setSelected('Physician Guided Package');
          break;
        case 'Self Guided Package':
          setSelected('Self Guided Package');
          break;
        case 'Vital Package':
          setSelected('Vital Package');
          break;
        case 'Physician Guided Package':
          setSelected('Physician Guided Package');
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, chartIsFetched]);

  // AUTO-SELECT FROM QUESTIONNAIRE PREFERENCE (first-time patients only)
  const autoSelectFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (autoSelectFiredRef.current) return;
    if (!products || !patientInfo?.navType?.initialPreference) return;
    if (chart?.PRODUCT_INFO?.selectedProduct) return; // returning patient

    const preference = patientInfo.navType?.initialPreference;
    const matchingProduct = products
      .filter((p) => p.active)
      .find((p) =>
        preference === 'self' ? p.name.toLowerCase().includes('self') : !p.name.toLowerCase().includes('self'),
      );

    if (matchingProduct) {
      autoSelectFiredRef.current = true;
      setSelected(matchingProduct.name);
    }
  }, [products, patientInfo, chart]);

  // REQUIRE CONFIRMATION ON PACKAGE CHANGE
  React.useEffect(() => {
    setProductConfirmed(false);
  }, [selected]);
  if (chartQuery.isLoading || patientDocQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && products?.length > 0) {
    return (
      <>
        <div id="product-selection-page" className="flex w-full flex-col items-center justify-center">
          <Heading id="product-selection-page-header" textContent="Select Your MIDEO Package" />
          <div id="product-cards-container" className="flex w-full flex-row flex-wrap justify-around">
            {products.map((product: Product) =>
              product.active ? (
                <button
                  type="button"
                  key={product.name}
                  onClick={() => {
                    handleSelection(product.name);
                  }}
                >
                  <ProductCard product={product} selected={selected} />
                </button>
              ) : null,
            )}
          </div>
          {patientInfo.isBulk && (
            <div className="my-4 flex flex-col items-center justify-center">
              <p className="text-2xl font-bold">
                *This price may be shared or covered by a partnership with your company/organization.
              </p>
            </div>
          )}
          {selected?.length > 0 && !productConfirmed && (
            <Button
              type="button"
              id="confirm-product"
              onClick={() => {
                setProductConfirmed(!productConfirmed);
                handleProductUpdate();
              }}
              textContent="Confirm Product Selection"
              disabled={!selected}
            />
          )}
          {selected && productConfirmed && (
            <button
              id="submit-form-info"
              title="Submit Form Info"
              onClick={handleNextButtonClick}
              disabled={false}
              className={classNames.nextButton}
            >
              Submit and Continue
            </button>
          )}
        </div>
      </>
    );
  }
}
export default OnboardingSelect;
